<script setup lang="ts">
import {
  $getChangeRequestComments,
  $getChangeRequestApprovalComments,
  $getChangeRequestSubtaskComments
} from '@/api/changeRequests'
import { useSnackbarStore } from '@/store/snackbar'
import type {
  ChangeRequestSubtaskComment,
  ChangeRequestComment,
  ChangeRequestApprovalComment
} from '@/models/changeRequest'
import type { TaggedUser } from '@/models/activity-timeline'

interface IProps {
  changeRequestId?: number | string | null | undefined,
  changeRequestApprovalId?: number | string | null | undefined,
  changeRequestSubtaskId?: number | string | null | undefined,
}

const props = withDefaults(defineProps<IProps>(), {
  changeRequestId: undefined,
  changeRequestApprovalId: undefined,
  changeRequestSubtaskId: undefined,
})

interface CustomEvents {
  (e: 'onAddComment'): void
}

const emit = defineEmits<CustomEvents>()

const snackbarStore = useSnackbarStore()

const route = useRoute()
const router = useRouter()

const placeholder = 'Write a comment...'

const isMounted = ref<boolean>(false)

// cro - Change Request Object (CR/CR Approval/CR Subtask)
const croComments = ref<ChangeRequestComment[] | ChangeRequestApprovalComment[] | ChangeRequestSubtaskComment[]>([])
const loading = ref<boolean>(false)

onMounted(() => {
  getCROComments()
  isMounted.value = true
})

const dialogTitle = computed(() => {
  if (props.changeRequestApprovalId) {
    return 'Change Request Approval Notes'
  }
  else if (props.changeRequestSubtaskId) {
    return 'Change Request Subtask Notes'
  }
  return 'Change Request Notes'
})

const getCROComments = async () => {
  if (props.changeRequestId) {
    await getCRComments()
  }
  else if (props.changeRequestApprovalId) {
    await getCRAComments()
  }
  else if (props.changeRequestSubtaskId) {
    await getCRSComments()
  }
}

const getCRComments = async () => {
  if (!props.changeRequestId) return

  loading.value = true

  try {
    const res = await $getChangeRequestComments({
      cr: props.changeRequestId
    })
    croComments.value = res.data.results
  } catch (e: any) {
    console.log(e)
  } finally {
    loading.value = false
  }
}

const getCRAComments = async () => {
  if (!props.changeRequestApprovalId) return

  loading.value = true

  try {
    const res = await $getChangeRequestApprovalComments({
      cr_approval: props.changeRequestApprovalId
    })
    croComments.value = res.data.results
  } catch (e: any) {
    console.log(e)
  } finally {
    loading.value = false
  }
}

const getCRSComments = async () => {
  if (!props.changeRequestSubtaskId) return

  loading.value = true

  try {
    const res = await $getChangeRequestSubtaskComments({
      cr_subtask: props.changeRequestSubtaskId
    })
    croComments.value = res.data.results
  } catch (e: any) {
    console.log(e)
  } finally {
    loading.value = false
  }
}

// Function to highlight tagged users
const highlightTaggedUsers = (content: string, taggedUsers: TaggedUser[], commentId: string): string => {
  // Replace tagged users with highlighted format
  taggedUsers.forEach(taggedUser => {
    content = content.replaceAll(
      `@${taggedUser.id}`,
      `<span class="highlight-tag-user tag-user-${commentId}-${taggedUser.id}">@${taggedUser.first_name} ${taggedUser.last_name}</span>`
    )
  })
  return content
}
</script>

<template>
  <div>
    <VCard style="max-height: 80vh; overflow-y: scroll">
      <template #title>
        <div class="d-flex align-center">
          {{ dialogTitle }}
        </div>
      </template>
      <VCardText>
        <VOverlay
          v-model="loading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </VOverlay>
        <VRow style="margin-bottom: 70px">
          <VCol
            v-for="(croComment, croCommentIdx) in croComments"
            :key="croCommentIdx"
            cols="12"
          >
            <VCard variant="tonal" class="pa-3">
              <div class="d-flex align-start gap-3">
                <VAvatar color="primary" size="36">
                  <VIcon icon="tabler-user" size="20" />
                </VAvatar>

                <div class="flex-grow-1">
                  <div class="d-flex align-center mb-1">
                    <span class="font-weight-medium text-body-1">
                      {{ croComment.user_name }}
                    </span>
                    <VSpacer />
                    <span class="text-caption text-medium-emphasis">
                      <VIcon icon="tabler-clock" size="14" class="mr-1" />
                      {{ croComment.created_display }}
                    </span>
                  </div>

                  <div
                    v-html="highlightTaggedUsers(croComment.comment, croComment.tagged_users_info, croComment.id)"
                    class="text-body-2"
                  />
                  <VMenu
                    v-for="(taggedUser, taggedUserKey) in croComment.tagged_users"
                    :key="taggedUserKey"
                    :activator="`.tag-user-${croComment.id}-${taggedUser.id}`"
                    location="top center"
                    open-on-hover
                    open-delay="100"
                    close-delay="0"
                    offset="5"
                    class="default-tooltip"
                  >
                    <span class="px-2 text-center">
                      {{ taggedUser.email }}
                    </span>
                  </VMenu>
                </div>
              </div>
            </VCard>
          </VCol>
          <VCol
            v-if="croComments?.length === 0"
            class="text-center"
          >
            <VAlert show>
              Notes not found
            </VAlert>
          </VCol>
        </VRow>
        <div class="change-request-object-notes-bottom-card">
          <VCard class="py-3">
            <CommentTagInput
              :change-request-id="changeRequestId"
              :change-request-approval-id="changeRequestApprovalId"
              :change-request-subtask-id="changeRequestSubtaskId"
              :placeholder="'Write a note...'"
              @onAddComment="getCROComments(); emit('onAddComment')"
            />
          </VCard>
        </div>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
.change-request-object-notes-bottom-card {
  transition: width 0.3s ease, right 0.3s ease; /* Transition width and right properties */
  position: fixed;
  width: 100%;
  bottom: 0;
  left: 0; /* Initially hide */
  .add-comment-btn-loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%); // centering
  }
}
</style>
